/**
 * @file GeneralSocialRiskRegisterListView.jsx
 * @author Fernando Ruano
 * @description General Social Risk Register List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function GeneralSocialRiskRegisterListView({
  dashboardStaffActions,
  clinicId
}) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getGeneralSocialRiskRegisterListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('Fecha de la última valoración trabajo social'),
      dataIndex: 'ultimo_registro_riesgo_psicosocial_psicologia',
      filter: {
        listName: 'listaFechaUltimaValoracion',
        searchName: 'filterFechaUltimaValoracion',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus(
          'ultimo_registro_riesgo_psicosocial_psicologia'
        ),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaUltimaValoracion',
            searchName: 'filterFechaUltimaValoracion'
          },
          dataIndex: 'ultimo_registro_riesgo_psicosocial_psicologia'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('Fecha último riesgo general y social'),
      dataIndex: 'ultimo_registro_revision_psicologia',
      filter: {
        listName: 'listaFechaUltimoRiesgoGeneralSocial',
        searchName: 'filterFechaUltimoRiesgoGeneralSocial',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('ultimo_registro_revision_psicologia'),
        multiple: 17
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaUltimoRiesgoGeneralSocial',
            searchName: 'filterFechaUltimoRiesgoGeneralSocial'
          },
          dataIndex: 'ultimo_registro_revision_psicologia'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        coincideRegistrosSeleccionado: classification,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };

    getGeneralSocialRiskRegisterListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            ultimo_registro_revision_psicologia:
              item?.ultimo_registro_revision_psicologia
                ? formatDate(item.ultimo_registro_revision_psicologia)
                : '',
            ultimo_registro_riesgo_psicosocial_psicologia:
              item?.ultimo_registro_riesgo_psicosocial_psicologia
                ? formatDate(item.ultimo_registro_riesgo_psicosocial_psicologia)
                : ''
          };
        });
        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      additionalColumns={additionalColumns}
      onPaginationChange={getetData}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
GeneralSocialRiskRegisterListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getGeneralSocialRiskRegisterListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default GeneralSocialRiskRegisterListView;
